import {useEffect, useLayoutEffect, useState} from 'react'

interface ScrollbarSizes {
  horizontalScrollbarSize: number | null
  verticalScrollbarSize: number | null
}

const DEFAULT_SIZES: ScrollbarSizes = {horizontalScrollbarSize: null, verticalScrollbarSize: null}

export function useMeasureScrollbars<E extends HTMLElement>(measureScrollbarsRef: React.RefObject<E | null>) {
  const [sizes, setSizes] = useState<ScrollbarSizes>(DEFAULT_SIZES)

  // Read initial sizes synchronously before paint
  useLayoutEffect(() => {
    const element = measureScrollbarsRef.current
    if (!element) return
    setSizes({
      horizontalScrollbarSize: element.offsetHeight - element.clientHeight,
      verticalScrollbarSize: element.offsetWidth - element.clientWidth,
    })
  }, [measureScrollbarsRef]) // Empty deps - refs are stable, run once on mount

  // ResizeObserver for subsequent updates (async, off render path)
  useEffect(() => {
    const element = measureScrollbarsRef.current
    if (!element) return

    const observer = new ResizeObserver(() => {
      // Use ref.current to get fresh element reference
      const el = measureScrollbarsRef.current
      if (!el) return

      const horizontalScrollbarSize = el.offsetHeight - el.clientHeight
      const verticalScrollbarSize = el.offsetWidth - el.clientWidth

      setSizes(prev => {
        if (
          prev.horizontalScrollbarSize !== horizontalScrollbarSize ||
          prev.verticalScrollbarSize !== verticalScrollbarSize
        ) {
          return {horizontalScrollbarSize, verticalScrollbarSize}
        }
        return prev
      })
    })

    observer.observe(element)
    return () => {
      observer.unobserve(element)
      observer.disconnect()
    }
  }, [measureScrollbarsRef]) // Empty deps - refs are stable, run once on mount

  return sizes
}
